import React, { useMemo } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  ABC_AGGREGATION_OPTIONS,
  ABC_PERIOD_OPTIONS,
  COUNT_METRIC,
  FISCAL_YEAR_START_MONTHS,
  VARIANT_MODE_OPTIONS,
  XYZ_BASIS_OPTIONS,
  XYZ_GRANULARITY_OPTIONS,
} from "../constants";
import ABCXYZInfoTooltip from "./ABCXYZInfoTooltip";

const selectClass =
  "w-full px-2.5 py-1 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500";

const FieldLabel = ({ children, info = null }) => (
  <div className="flex items-center gap-1.5 mb-1">
    <label className="block text-xs font-medium text-gray-600">
      {children}
    </label>
    {info}
  </div>
);

/**
 * Inline configuration panel for Object Analysis.
 *
 * Every dropdown is populated from the dynamic /abc-xyz/object-config-options
 * metadata: object types, grouping attributes, numeric ABC metrics, date
 * fields grouped by origin (own vs. directly connected object types) and the
 * reserved process-start pseudo field. Nothing is hard-coded.
 *
 * The panel is reused by the Cross Matrix, which shares the same object type
 * and object configuration but additionally needs the variant grouping mode:
 * pass `showVariantMode` with `variantMode`/`onVariantModeChange` to reveal an
 * extra Variant Mode dropdown. Object Analysis passes neither, so its layout is
 * unchanged.
 */
const ObjectConfigPanel = ({
  configOptions,
  objectType,
  groupByAttribute,
  abcMetric,
  abcAggregation,
  dateField,
  dateFieldOrigin,
  xyzBasis,
  xyzGranularity = "month",
  abcPeriod = "last_12_months",
  fiscalYearStartMonth = 1,
  onObjectTypeChange,
  onGroupByAttributeChange,
  onAbcMetricChange,
  onAbcAggregationChange,
  onDateFieldChange,
  onXyzBasisChange,
  onXyzGranularityChange = null,
  onAbcPeriodChange = null,
  onFiscalYearStartMonthChange = null,
  onApply,
  loading = false,
  applyDisabledReason = null,
  collapsed = false,
  onToggleCollapsed = null,
  title = "Object Matrix Configuration",
  showVariantMode = false,
  variantMode = "exact",
  onVariantModeChange = null,
}) => {
  const objectTypesList = configOptions?.object_types || [];
  const groupAttributes =
    (objectType && configOptions?.attributes_by_object_type?.[objectType]) ||
    [];
  const numericAttributes =
    (objectType && configOptions?.numeric_attributes?.[objectType]) || [];
  const ownDateAttributes =
    (objectType && configOptions?.date_attributes?.[objectType]) || [];
  const connectedDates =
    (objectType && configOptions?.connected_date_attributes?.[objectType]) ||
    {};
  const processStartField = configOptions?.process_start_field;

  // Date options carry their origin, encoded as "origin||field" so the
  // select stays a single control while the backend still receives both.
  const dateOptions = useMemo(() => {
    const options = [];
    if (processStartField) {
      options.push({
        group: objectType || "Selected object type",
        value: `${objectType}||${processStartField.name}`,
        label: processStartField.label,
      });
    }
    ownDateAttributes.forEach((attr) =>
      options.push({
        group: objectType,
        value: `${objectType}||${attr}`,
        label: attr,
      }),
    );
    Object.entries(connectedDates).forEach(([connectedType, attrs]) =>
      (attrs || []).forEach((attr) =>
        options.push({
          group: `${connectedType} (connected)`,
          value: `${connectedType}||${attr}`,
          label: attr,
        }),
      ),
    );
    return options;
  }, [processStartField, objectType, ownDateAttributes, connectedDates]);

  const groupedDateOptions = useMemo(() => {
    const groups = new Map();
    dateOptions.forEach((option) => {
      if (!groups.has(option.group)) groups.set(option.group, []);
      groups.get(option.group).push(option);
    });
    return [...groups.entries()];
  }, [dateOptions]);

  const dateValue =
    dateField && (dateFieldOrigin || objectType)
      ? `${dateFieldOrigin || objectType}||${dateField}`
      : "";

  const handleDateChange = (encoded) => {
    if (!encoded) {
      onDateFieldChange("", "");
      return;
    }
    const [origin, ...fieldParts] = encoded.split("||");
    onDateFieldChange(fieldParts.join("||"), origin);
  };

  const metricIsCount = abcMetric === COUNT_METRIC;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2 no-drag">
      <button
        type="button"
        onClick={() => onToggleCollapsed?.()}
        className="w-full flex items-center gap-1.5 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
        aria-expanded={!collapsed}
      >
        {collapsed ? (
          <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
        )}
        <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">
          {title}
        </span>
      </button>

      {!collapsed && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2">
            <div>
              <FieldLabel>Object Type</FieldLabel>
              <select
                value={objectType}
                onChange={(e) => onObjectTypeChange(e.target.value)}
                className={selectClass}
                disabled={loading || objectTypesList.length === 0}
              >
                <option value="">Select object type…</option>
                {objectTypesList.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            {showVariantMode && (
              <div>
                <FieldLabel>Variant Mode</FieldLabel>
                <select
                  value={variantMode}
                  onChange={(e) => onVariantModeChange?.(e.target.value)}
                  className={selectClass}
                  disabled={loading}
                >
                  {VARIANT_MODE_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <FieldLabel>Group By</FieldLabel>
              <select
                value={groupByAttribute}
                onChange={(e) => onGroupByAttributeChange(e.target.value)}
                className={selectClass}
                disabled={loading || !objectType}
              >
                <option value="">Select attribute…</option>
                {groupAttributes.map((attr) => (
                  <option key={attr} value={attr}>
                    {attr}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel
                info={
                  <ABCXYZInfoTooltip sections={["abc"]} title="ABC Metric" />
                }
              >
                ABC Metric
              </FieldLabel>
              <select
                value={abcMetric}
                onChange={(e) => onAbcMetricChange(e.target.value)}
                className={selectClass}
                disabled={loading || !objectType}
              >
                <option value={COUNT_METRIC}>Count (objects per group)</option>
                {numericAttributes.map((attr) => (
                  <option key={attr} value={attr}>
                    {attr}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel>ABC Aggregation</FieldLabel>
              <select
                value={metricIsCount ? "count" : abcAggregation}
                onChange={(e) => onAbcAggregationChange(e.target.value)}
                className={selectClass}
                disabled={loading || metricIsCount}
              >
                {ABC_AGGREGATION_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel>Date Field</FieldLabel>
              <select
                value={dateValue}
                onChange={(e) => handleDateChange(e.target.value)}
                className={selectClass}
                disabled={loading || !objectType}
              >
                <option value="">Select date field…</option>
                {groupedDateOptions.map(([group, options]) => (
                  <optgroup key={group} label={group}>
                    {options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel
                info={
                  <ABCXYZInfoTooltip sections={["xyz"]} title="XYZ Basis" />
                }
              >
                XYZ Basis
              </FieldLabel>
              <select
                value={metricIsCount ? "count" : xyzBasis}
                onChange={(e) => onXyzBasisChange(e.target.value)}
                className={selectClass}
                disabled={loading || metricIsCount}
              >
                {XYZ_BASIS_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel
                info={
                  <ABCXYZInfoTooltip
                    sections={["analysisPeriod"]}
                    title="Analysis Period"
                  />
                }
              >
                Analysis Period
              </FieldLabel>
              <select
                value={abcPeriod}
                onChange={(e) => onAbcPeriodChange?.(e.target.value)}
                className={selectClass}
                disabled={loading}
              >
                {ABC_PERIOD_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel
                info={
                  <ABCXYZInfoTooltip
                    sections={["calculationGranularity"]}
                    title="Calculation Granularity"
                  />
                }
              >
                Calculation Granularity
              </FieldLabel>
              <select
                value={xyzGranularity}
                onChange={(e) => onXyzGranularityChange?.(e.target.value)}
                className={selectClass}
                disabled={loading}
              >
                {XYZ_GRANULARITY_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {/* Apply stays a grid cell — one column wide, bottom-aligned —
                so it keeps the width of the dropdown above it. The explicit
                column start pins it to the LAST column so it never drifts to
                the left on an incomplete final row, regardless of exactly
                how many fields precede it (Object mode's base count vs. the
                Cross Matrix's extra Variant Mode dropdown, plus the
                conditional Financial Year Starts field below). */}
            {abcPeriod === "fiscal_year" && (
              <div>
                <FieldLabel>Financial Year Starts</FieldLabel>
                <select
                  value={fiscalYearStartMonth}
                  onChange={(e) =>
                    onFiscalYearStartMonthChange?.(Number(e.target.value))
                  }
                  className={selectClass}
                  disabled={loading}
                >
                  {FISCAL_YEAR_START_MONTHS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex items-end sm:col-start-2 lg:col-start-4">
              <button
                type="button"
                onClick={onApply}
                disabled={loading || Boolean(applyDisabledReason)}
                title={applyDisabledReason || undefined}
                className="w-full px-4 py-1.5 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? "Calculating…" : "Apply"}
              </button>
            </div>
          </div>
          {applyDisabledReason && (
            <p className="text-xs text-gray-500">{applyDisabledReason}</p>
          )}
        </>
      )}
    </div>
  );
};

export default ObjectConfigPanel;
